import { useState } from "react";
import { GiBiceps } from "react-icons/gi";
import { sendContactForm } from "../../services/Contact";
import styles from "./ContactForm.module.css";
import TurnstileWidget from "./TurnstileWidget";

const initialFields = {
	name: "",
	email: "",
	phone: "",
	company: "",
	interest: "",
	message: "",
	website: "",
};

function ContactForm() {
	const [fields, setFields] = useState(initialFields);
	const [status, setStatus] = useState("idle");
	const [feedback, setFeedback] = useState("");
	const [turnstileToken, setTurnstileToken] = useState(null);
	const [turnstileResetKey, setTurnstileResetKey] = useState(0);

	const updateField = (event) => {
		const { name, value } = event.target;
		setFields((current) => ({ ...current, [name]: value }));
	};

	const handleSubmit = async (event) => {
		event.preventDefault();

		const name = fields.name.trim();
		const email = fields.email.trim();
		const message = fields.message.trim();

		if (name.length < 2) {
			setStatus("error");
			setFeedback("Please enter your name.");
			return;
		}

		if (!turnstileToken) {
			setStatus("error");
			setFeedback("Please complete the security check.");
			return;
		}

		setStatus("sending");
		setFeedback("");

		try {
			const response = await sendContactForm({
				source: "aftrprty",
				name,
				email,
				phone: fields.phone.trim(),
				message,
				turnstileToken,
				details: {
					company: fields.company.trim(),
					interest: fields.interest,
				},
				website: fields.website,
			});

			setFields(initialFields);
			setStatus("success");
			setFeedback(response.message || "Thanks. Your inquiry has been sent.");
		} catch (error) {
			setStatus("error");
			setFeedback(error.message || "Your inquiry could not be sent. Please try again.");
		} finally {
			setTurnstileToken(null);
			setTurnstileResetKey((current) => current + 1);
		}
	};

	return (
		<form className={styles.form} onSubmit={handleSubmit}>
			<div className={styles.heading}>
				<span>General inquiry</span>
				<strong>Orders promised by 11/01/26</strong>
			</div>

			<div className={styles.fields}>
				<label className={styles.field}>
					<span>Name</span>
					<input
						required
						minLength="2"
						name="name"
						autoComplete="name"
						value={fields.name}
						onChange={updateField}
						placeholder="Your name"
					/>
				</label>

				<label className={styles.field}>
					<span>Email</span>
					<input
						required
						type="email"
						name="email"
						autoComplete="email"
						value={fields.email}
						onChange={updateField}
						placeholder="you@example.com"
					/>
				</label>

				<label className={styles.field}>
					<span>Phone <em>optional</em></span>
					<input
						type="tel"
						name="phone"
						autoComplete="tel"
						value={fields.phone}
						onChange={updateField}
						placeholder="+1 803 555 1212"
					/>
				</label>

				<label className={styles.field}>
					<span>Company or gym <em>optional</em></span>
					<input
						name="company"
						autoComplete="organization"
						value={fields.company}
						onChange={updateField}
						placeholder="Your organization"
					/>
				</label>

				<label className={`${styles.field} ${styles.fullWidth}`}>
					<span>What can we help with? <em>optional</em></span>
					<select name="interest" value={fields.interest} onChange={updateField}>
						<option value="">Select a topic</option>
						<option value="Order question">Order question</option>
						<option value="Gym or team order">Gym or team order</option>
						<option value="Retail inquiry">Retail inquiry</option>
						<option value="Brand partnership">Brand partnership</option>
						<option value="Other">Other</option>
					</select>
				</label>

				<label className={`${styles.field} ${styles.fullWidth}`}>
					<span>Message</span>
					<textarea
						required
						rows="5"
						name="message"
						value={fields.message}
						onChange={updateField}
						placeholder="Tell us what you would like to know."
					/>
				</label>
			</div>

			<label className={styles.honeypot} aria-hidden="true">
				Website
				<input
					tabIndex="-1"
					name="website"
					autoComplete="off"
					value={fields.website}
					onChange={updateField}
				/>
			</label>

			<div className={styles.turnstile}>
				<TurnstileWidget key={turnstileResetKey} onTokenChange={setTurnstileToken} />
			</div>

			<div className={styles.actions}>
				<p className={status === "error" ? styles.error : ""} aria-live="polite">
					{feedback}
				</p>
				<button type="submit" disabled={status === "sending" || !turnstileToken}>
					<GiBiceps className={styles.submitIcon} aria-hidden="true" />
					<span>{status === "sending" ? "Sending..." : "Send inquiry"}</span>
					<GiBiceps className={`${styles.submitIcon} ${styles.submitIconRight}`} aria-hidden="true" />
				</button>
			</div>
		</form>
	);
}

export default ContactForm;
